'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Coins, Mountain, TreePine, Sparkles } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function AsDeniersPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Deniers</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">As</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/deniers"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Deniers'e Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl p-8 mb-8 border border-green-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-green-100 to-emerald-50 border border-green-300 flex flex-col items-center justify-center relative">
                {/* Card Visual Design */}
                <div className="text-center">
                  <div className="text-6xl mb-4">💰</div>
                  <div className="w-16 h-1 bg-green-600 mx-auto mb-4"></div>
                  <div className="text-2xl font-bold text-green-800">AS</div>
                  <div className="text-sm text-green-700 mt-2">de Deniers</div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 text-green-600">
                  <Coins className="h-4 w-4" />
                </div>
                <div className="absolute top-4 right-4 text-green-600">
                  <Coins className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 left-4 text-green-600">
                  <Mountain className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 right-4 text-green-600">
                  <Mountain className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">AS</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">As de Deniers</h1>
                  <p className="text-xl text-green-400">Yeni Fırsat / Maddi Başlangıç</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde toprak elementinin en saf hali. Maddi gücün kaynağı,
                yeni yatırımların ve pratik projelerin başlangıcını simgeler. Güvenlik
                ve istikrarın el uzattığı andır.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Yeni Fırsat</span>
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Maddi Güvenlik</span>
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Pratiklik</span>
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">İstikrar</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Coins className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Finansal Fırsat</h3>
                  <p className="text-slate-400 text-sm">Yeni gelir kaynağı, karlı yatırım fırsatı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Maddi Güvenlik</h3>
                  <p className="text-slate-400 text-sm">İstikrarlı gelir, finansal temel oluşturma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Pratik Başarı</h3>
                  <p className="text-slate-400 text-sm">Elle tutulur sonuçlar, somut kazanımlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sağlık ve Canlılık</h3>
                  <p className="text-slate-400 text-sm">Fiziksel iyilik hali, doğal yaşam.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Mountain className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kaçırılan Fırsat</h3>
                  <p className="text-slate-400 text-sm">Maddi fırsatları değerlendirememe.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Finansal Güvensizlik</h3>
                  <p className="text-slate-400 text-sm">Gelir belirsizliği, istikrarsızlık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Materyalizm</h3>
                  <p className="text-slate-400 text-sm">Sadece para odaklı düşünme, açgözlülük.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sağlık Sorunları</h3>
                  <p className="text-slate-400 text-sm">Fiziksel yorgunluk, doğadan kopma.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Coins className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Sikke/Denier</h3>
              <p className="text-slate-400 text-sm">Maddi değer, servet ve pratik güç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mountain className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Toprak Elementi</h3>
              <p className="text-slate-400 text-sm">İstikrar, güvenlik ve somut gerçeklik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">As Enerjisi</h3>
              <p className="text-slate-400 text-sm">Saf maddi potansiyel ve yeni fırsat.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve Finans
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni iş fırsatları</li>
                <li>• Yatırım projeleri</li>
                <li>• Gayrimenkul alımı</li>
                <li>• Maaş artışı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <TreePine className="h-5 w-5 text-green-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Sağlıklı yaşam başlangıcı</li>
                <li>• Doğal beslenme</li>
                <li>• Fiziksel aktivite</li>
                <li>• Çevre dostu yaşam</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/deniers"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Deniers Takımı
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/deniers/deux"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Deux de Deniers
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}